// eslint-disable-next-line import/no-extraneous-dependencies
import { TransactionConfig } from 'web3-core';
import Web3 from 'web3';
import {
  COMPOUNDING_CONTRACT_ADDRESS,
  EXCESS_INHIBITOR,
  COMPOUNDING_FEE_ADDITION,
} from '../../utils/envVars';
import BigNumber from 'bignumber.js';

export type Queue = {
  length: BigNumber;
  fee: BigNumber;
};

export type TxConfigQueue = {
  transactionParams: TransactionConfig;
  queue: Queue;
};

export const getCompoundingQueueLength = async (
  web3: Web3
): Promise<BigNumber> => {
  let queueLengthHex;
  try {
    queueLengthHex = await web3.eth.getStorageAt(
      COMPOUNDING_CONTRACT_ADDRESS!,
      0 // EXCESS_WITHDRAWAL_REQUESTS_STORAGE_SLOT
    );

    if (!queueLengthHex) {
      throw new Error('Unable to get compounding queue length');
    }
    if (queueLengthHex === EXCESS_INHIBITOR) {
      throw new Error('Compounding queue is disabled');
    }
  } catch (error) {
    console.error(error);
    queueLengthHex = '0x0';
  }

  return new BigNumber(queueLengthHex);
};

const getRequiredFee = (queueLength: BigNumber): BigNumber => {
  let i = new BigNumber(1);
  let output = new BigNumber(0);
  let numeratorAccum = new BigNumber(1).times(17); // factor * denominator

  while (numeratorAccum.gt(0)) {
    output = output.plus(numeratorAccum);
    numeratorAccum = numeratorAccum.times(queueLength).dividedBy(i.times(17));
    i = i.plus(1);
  }

  return output.dividedToIntegerBy(17);
};

// https://eips.ethereum.org/EIPS/eip-7251#fee-calculation
export const getCompoundingQueue = async (web3: Web3): Promise<Queue> => {
  const length = await getCompoundingQueueLength(web3);

  // Add to the queue length to reduce the likelihood of transaction failure
  const appendedFeeSize = length.plus(COMPOUNDING_FEE_ADDITION);

  const fee = getRequiredFee(appendedFeeSize);

  return { length, fee };
};

export const generateCompoundParams = async (
  web3: Web3,
  address: string,
  source: string,
  target: string
): Promise<TxConfigQueue> => {
  const queue = await getCompoundingQueue(web3);

  // https://eips.ethereum.org/EIPS/eip-7251#add-consolidation-request
  const transactionParams = {
    to: COMPOUNDING_CONTRACT_ADDRESS,
    from: address,
    // calldata (96 bytes): sourceValidator.pubkey (48 bytes) + targetValidator.pubkey (48 bytes)
    data: `0x${source.substring(2)}${target.substring(2)}`,
    value: queue.fee.toFixed(0),
    gas: 200000,
  };

  return { transactionParams, queue };
};
